import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  MapPin, RefreshCw, ShieldAlert, Users, LogIn, Globe2,
  Search, Smartphone, Tablet, Monitor, Bot, ChevronDown, ChevronRight,
  Wrench, Check, X as XIcon,
} from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';

import { API_BASE as API } from '../../config';

const WORLD_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
  { value: 0, label: 'All time' },
];

const DEVICE_ICON = { Mobile: Smartphone, Tablet, Desktop: Monitor, Bot };

function deviceIcon(t) {
  const I = DEVICE_ICON[t] || Monitor;
  return <I className="w-3.5 h-3.5 text-slate-400" />;
}

function StatCard({ icon: Icon, label, value, accent = 'text-emerald-400', testid }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

/** World map of login origins. Pins are rose-red when any login from that
 *  country was VPN/proxy/Tor-flagged, emerald otherwise. Radius scales with
 *  √(count) so a single login still shows but doesn't dwarf the busy ones.
 */
function LoginMap({ countries }) {
  const maxCount = countries.reduce((m, c) => Math.max(m, c.count), 1);
  return (
    <ComposableMap projectionConfig={{ scale: 145 }} width={900} height={420} style={{ width: '100%', height: 'auto' }}>
      <Geographies geography={WORLD_TOPO}>
        {({ geographies }) =>
          geographies.map((geo) => (
            <Geography
              key={geo.rsmKey} geography={geo}
              fill="#1e293b" stroke="#334155" strokeWidth={0.4}
              style={{
                default: { outline: 'none' },
                hover: { fill: '#334155', outline: 'none' },
                pressed: { outline: 'none' },
              }}
            />
          ))
        }
      </Geographies>
      {countries.filter(c => c.lat != null && c.lon != null).map((c) => {
        const r = 4 + 14 * Math.sqrt(c.count / maxCount);
        const flagged = c.has_suspicious;
        const fill = flagged ? '#f43f5e' : '#10b981';  // rose-500 | emerald-500
        const tooltip = `${c.country}: ${c.count} login${c.count === 1 ? '' : 's'}${flagged ? ` · ⚑ ${c.suspicious} flagged` : ''}`;
        return (
          <Marker key={`${c.country}-${c.lat}-${c.lon}`} coordinates={[c.lon, c.lat]}>
            <circle r={r} fill={fill} fillOpacity={0.35} stroke={fill} strokeWidth={1.5}>
              <title>{tooltip}</title>
            </circle>
            <text textAnchor="middle" y={-r - 2} className="fill-white" style={{ fontSize: 9, fontWeight: 600, pointerEvents: 'none' }}>
              {c.count > 2 ? c.count : ''}
            </text>
          </Marker>
        );
      })}
    </ComposableMap>
  );
}

/** Inline "Run IP detection diagnostic" widget. Click → hit
 *  `/api/dev/debug/my-ip`, render the resolved IP, headers, and a clear
 *  Healthy/Issue verdict so the operator never needs to touch curl.
 */
function IpDiagnostic() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const res = await axios.get(`${API}/dev/debug/my-ip`, { withCredentials: true });
      setResult(res.data);
      setOpen(true);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Diagnostic failed' });
      setOpen(true);
    } finally { setRunning(false); }
  };

  // Heuristic: if the resolved IP equals the direct connection IP (i.e. no
  // forwarding header set), or matches a known datacenter pattern, treat as
  // "not preserving real client IP" so the operator knows to dig deeper.
  const looksProxied = result && result.resolved_client_ip !== result.direct_connection;
  const ip = result?.resolved_client_ip;
  const isPrivate = ip && (
    ip.startsWith('10.') || ip.startsWith('192.168.') ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(ip) || ip === '127.0.0.1'
  );
  const healthy = result && ip && !isPrivate && looksProxied;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mb-6" data-testid="ip-diagnostic">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">Network diagnostic</h3>
          <span className="text-xs text-slate-500 hidden sm:inline">
            Verify the ingress is preserving real client IPs
          </span>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={run} disabled={running}
          className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
          data-testid="run-ip-diagnostic"
        >
          {running ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1.5" />}
          Run check
        </Button>
      </div>

      {open && result && !result.error && (
        <div className="mt-4 space-y-3 text-xs">
          <div className={`p-3 rounded border ${healthy
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}>
            <div className="flex items-center gap-2 font-semibold mb-1">
              {healthy ? <Check className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
              {healthy
                ? 'Real client IP is being preserved ✓'
                : 'Real client IP may not be reaching the backend'}
            </div>
            <p className="text-slate-300 leading-snug">
              {healthy
                ? 'cf-connecting-ip / x-forwarded-for headers are populated and IPinfo resolved them. The User Logins map will show real visitor origins.'
                : 'Direct connection IP matched the resolved IP, which means no proxy/CDN header was set. If this is production, your edge isn\'t forwarding client IPs — see hint below.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-slate-950 border border-slate-800 rounded p-3">
              <p className="text-slate-500 uppercase text-[10px] tracking-wider mb-1">Resolved client IP</p>
              <p className="text-base font-mono text-white" data-testid="diag-resolved-ip">
                {result.resolved_client_ip || '—'}
              </p>
              {result.geo?.city && (
                <p className="text-slate-400 mt-1">
                  {result.geo.city}, {result.geo.region || ''} {result.geo.country_code ? `(${result.geo.country_code})` : ''}
                </p>
              )}
              {result.geo?.org && <p className="text-slate-500 truncate">{result.geo.org}</p>}
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded p-3">
              <p className="text-slate-500 uppercase text-[10px] tracking-wider mb-1">Direct connection</p>
              <p className="text-base font-mono text-white">{result.direct_connection || '—'}</p>
              <p className="text-slate-500 mt-1">
                Usually the load balancer or ingress, not the visitor.
              </p>
            </div>
          </div>

          <details className="bg-slate-950 border border-slate-800 rounded p-3">
            <summary className="cursor-pointer text-slate-400 hover:text-white text-xs font-semibold select-none">
              All headers seen ({Object.keys(result.headers_seen || {}).length})
            </summary>
            <pre className="mt-2 text-[10px] text-slate-400 overflow-x-auto font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(result.headers_seen, null, 2)}
            </pre>
          </details>

          <p className="text-[10px] text-slate-500 leading-snug">{result.hint}</p>

          <button
            onClick={() => setOpen(false)}
            className="text-[11px] text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
          >
            <XIcon className="w-3 h-3" /> Hide
          </button>
        </div>
      )}

      {open && result?.error && (
        <p className="mt-3 text-xs text-rose-400">Diagnostic failed: {result.error}</p>
      )}
    </div>
  );
}

export default function DevUserLogins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [mapOpen, setMapOpen] = useState(() => {
    const v = window.localStorage.getItem('userLogins.mapOpen');
    return v === null ? true : v === 'true';
  });
  useEffect(() => { window.localStorage.setItem('userLogins.mapOpen', String(mapOpen)); }, [mapOpen]);

  const fetchData = useCallback(async (rangeDays) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dev/user-logins?days=${rangeDays}`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(days); }, [fetchData, days]);

  const filteredRecent = useMemo(() => {
    const rows = data?.recent || [];
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter((e) => [
      e.user_email, e.user_name, e.ip_address,
      e.geo?.city, e.geo?.country, e.geo?.region, e.geo?.org,
      e.device?.summary, e.device?.browser, e.device?.os,
    ].filter(Boolean).join(' ').toLowerCase().includes(s));
  }, [data, search]);

  if (loading && !data) return <div className="text-center py-12 text-slate-400">Loading login data…</div>;
  if (!data) return <div className="text-center py-12 text-slate-400">No login data</div>;

  const rangeLabel = RANGE_OPTIONS.find(o => o.value === days)?.label || '';

  return (
    <div className="text-slate-200">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MapPin className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">User Logins</h1>
            <p className="text-sm text-slate-400">Where real users sign in from. Operator-only view.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[160px] h-9 bg-slate-900 border-slate-700 text-slate-200" data-testid="logins-range-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              {RANGE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={String(o.value)} data-testid={`logins-range-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => fetchData(days)} className="text-slate-400 hover:text-white" data-testid="refresh-logins">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={LogIn} label="Total Logins" value={data.total_logins} testid="stat-total-logins" />
        <StatCard icon={Users} label="Unique Users" value={data.unique_users} accent="text-blue-400" testid="stat-unique-users" />
        <StatCard icon={Globe2} label="Countries" value={data.countries?.length || 0} testid="stat-countries" />
        <StatCard icon={ShieldAlert} label="VPN / Proxy / Tor"
          value={data.suspicious || 0}
          accent={(data.suspicious || 0) > 0 ? 'text-rose-400' : 'text-slate-500'}
          testid="stat-suspicious"
        />
      </div>

      {/* IP-detection diagnostic — replaces the curl instructions */}
      <IpDiagnostic />

      {/* Collapsible map */}
      <div className="bg-slate-950 border border-slate-800 rounded-lg mb-6" data-testid="logins-map">
        <button
          onClick={() => setMapOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-900 transition-colors rounded-t-lg"
          data-testid="logins-map-toggle"
        >
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            {mapOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
            <Globe2 className="w-4 h-4 text-emerald-400" /> Login origins ({rangeLabel})
            <span className="text-xs text-slate-500 font-normal ml-1">({data.countries?.length || 0} countries)</span>
          </h3>
          {!mapOpen && data.countries?.length > 0 && (
            <span className="text-xs text-slate-500 hidden sm:inline">
              {data.countries.slice(0, 3).map(c => `${c.country_code || c.country?.slice(0, 2)} ${c.count}`).join(' · ')}
              {data.countries.length > 3 ? ` · +${data.countries.length - 3} more` : ''}
            </span>
          )}
        </button>
        {mapOpen && (
          <div className="px-3 pb-3">
            {data.countries?.length > 0 ? (
              <>
                <LoginMap countries={data.countries} />
                <div className="flex items-center gap-4 px-1 pt-2 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60 border border-emerald-400" /> Clean
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60 border border-rose-400" /> VPN / Proxy / Tor detected
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 py-8 text-center">No geo-enriched logins yet in this window.</p>
            )}
          </div>
        )}
      </div>

      {/* Top users + Top cities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="top-users-panel">
          <h3 className="text-sm font-semibold text-white mb-3">Top Users by Login Count</h3>
          <div className="space-y-2">
            {(data.top_users || []).map(u => (
              <div key={u.user_id} className="flex items-center justify-between text-xs gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-100 truncate font-medium">{u.name || u.email || '—'}</p>
                  <p className="text-slate-500 truncate">{u.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-white font-semibold">{u.count} logins</p>
                  <p className="text-slate-500 text-[10px]">{new Date(u.last_login).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
            {!data.top_users?.length && <p className="text-sm text-slate-500">No data</p>}
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="top-cities-panel">
          <h3 className="text-sm font-semibold text-white mb-3">Top Cities</h3>
          <div className="space-y-2">
            {(data.cities || []).slice(0, 12).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-300 truncate">
                  {c.city}{c.region ? `, ${c.region}` : ''}{c.country ? `, ${c.country}` : ''}
                </span>
                <span className="text-white font-semibold">{c.count}</span>
              </div>
            ))}
            {!data.cities?.length && <p className="text-sm text-slate-500">No data</p>}
          </div>
        </div>
      </div>

      {/* Recent logins table */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="text-sm font-semibold text-white">Recent Logins</h3>
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input
              placeholder="Filter by user, city, IP, device…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 bg-slate-950 border-slate-700 text-slate-200 placeholder:text-slate-600"
              data-testid="logins-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-800">
                <th className="text-left py-2 px-3">Time</th>
                <th className="text-left py-2 px-3">User</th>
                <th className="text-left py-2 px-3">Location</th>
                <th className="text-left py-2 px-3">Device</th>
                <th className="text-left py-2 px-3">IP / ISP</th>
                <th className="text-right py-2 px-3">Flags</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecent.map((e, i) => {
                const geo = e.geo || {};
                const dev = e.device || {};
                const flagged = geo.is_vpn || geo.is_proxy || geo.is_tor;
                const flags = [];
                if (geo.is_vpn) flags.push('VPN');
                if (geo.is_proxy) flags.push('PROXY');
                if (geo.is_tor) flags.push('TOR');
                if (geo.is_hosting) flags.push('HOST');
                if (dev.is_bot) flags.push('BOT');
                return (
                  <tr key={i}
                    className={`border-b border-slate-800/50 hover:bg-slate-800/40 ${flagged ? 'bg-rose-950/20' : ''}`}
                    data-testid={`login-row-${i}`}
                  >
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-slate-200">
                      <div className="flex flex-col">
                        <span className="font-medium">{e.user_name || '—'}</span>
                        <span className="text-slate-500 text-[10px]">{e.user_email}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-slate-300">
                      {geo.city || geo.country ? (
                        <span>
                          {geo.city}{geo.region && geo.city !== geo.region ? `, ${geo.region}` : ''}
                          {geo.country_code ? <span className="text-slate-500 ml-1">({geo.country_code})</span> : ''}
                        </span>
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-2 px-3 text-slate-300">
                      <div className="flex items-center gap-1.5">
                        {deviceIcon(dev.device)}
                        <span className="truncate max-w-[260px]" title={dev.summary || ''}>
                          {dev.summary || dev.device || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-slate-500">
                      <p className="font-mono">{e.ip_address || '—'}</p>
                      {geo.org && <p className="text-[10px] truncate max-w-[180px]" title={geo.org}>{geo.org}</p>}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {flags.length ? flags.map(f => (
                        <span key={f} className="inline-block ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-rose-500/15 text-rose-400 border border-rose-500/30">{f}</span>
                      )) : <span className="text-slate-700">·</span>}
                    </td>
                  </tr>
                );
              })}
              {!filteredRecent.length && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">No logins match</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {search && (
          <p className="text-xs text-slate-500 mt-2">
            Showing {filteredRecent.length} of {data.recent?.length || 0} logins
          </p>
        )}
      </div>
    </div>
  );
}
